import React, { useEffect, useState } from 'react';
import './ClientAddForm.css';

const TABS = [
  { id: 'customer', label: 'Client', icon: 'fa-building' },
  { id: 'admin', label: 'Administration', icon: 'fa-headset' },
  { id: 'manager', label: 'Manager', icon: 'fa-user-tie' },
];

function toPPPM(value) {
  const v = String(value || '').trim().toLowerCase();
  if (v === 'pm' || v === 'morale' || v === 'corporation') return 'PM';
  return 'PP';
}

function emptyForm() {
  return {
    name: '',
    num_chrono: '',
    vat_code: '',
    adresse: '',
    personne_type: 'PP',
    nh_type: 'E',
    administration_email: '',
    administration_phone_number: '',
    first_name: '',
    last_name: '',
    phone_numbers: '',
    emails: '',
    identity_type: '',
    identity_number: '',
    identity_issue_date: '',
    identity_expiray_date: '',
  };
}

export default function ClientEditForm({
  client,
  onClientUpdated,
  onCancel,
  nhLocked = false,
  userIsAdmin = false,
}) {
  const [activeTab, setActiveTab] = useState('customer');
  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState('');
  const [originalNh, setOriginalNh] = useState('E');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!client?.id) return;
      setLoadingData(true);
      setError('');
      setActiveTab('customer');

      const baseNh = client.nh_type || 'E';
      setOriginalNh(baseNh);
      setFormData({
        ...emptyForm(),
        name: client.nom || '',
        num_chrono:
          client.num_chrono != null && client.num_chrono !== ''
            ? String(client.num_chrono)
            : '',
        vat_code: client.matricule_fiscal || '',
        adresse: client.adresse || '',
        personne_type: toPPPM(client.personne_type),
        nh_type: baseNh,
        administration_email: client.email || '',
        administration_phone_number: client.telephone || '',
      });

      try {
        const res = await fetch(`/api/clients/${client.id}/personnel`, { cache: 'no-store' });
        const data = await res.json();
        if (!cancelled && res.ok && data.success) {
          setFormData((prev) => ({
            ...prev,
            administration_email:
              data.contact?.administration_email || prev.administration_email || '',
            administration_phone_number:
              data.contact?.administration_phone_number || prev.administration_phone_number || '',
            first_name: data.manager?.first_name || '',
            last_name: data.manager?.last_name || '',
            phone_numbers: data.manager?.phone_numbers || '',
            emails: data.manager?.emails || '',
            identity_type: data.identity?.identity_type || '',
            identity_number: data.identity?.identity_number || '',
            identity_issue_date: (data.identity?.identity_issue_date || '').slice(0, 10),
            identity_expiray_date: (data.identity?.identity_expiray_date || '').slice(0, 10),
          }));
        }
      } catch {
        /* ignore: form still works with client list fields */
      } finally {
        if (!cancelled) setLoadingData(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [client]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'nh_type' && nhLocked) return;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!client?.id || loading) return;
    setLoading(true);
    setError('');

    const name = formData.name.trim();
    if (!name) {
      setError('Le nom du client est obligatoire.');
      setActiveTab('customer');
      setLoading(false);
      return;
    }

    const adminEmail = formData.administration_email.trim();
    if (adminEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adminEmail)) {
      setError('E-mail administration invalide.');
      setActiveTab('admin');
      setLoading(false);
      return;
    }

    const mgrEmail = formData.emails.trim();
    if (mgrEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mgrEmail)) {
      setError('E-mail manager invalide.');
      setActiveTab('manager');
      setLoading(false);
      return;
    }

    const editedNh = formData.nh_type || 'E';
    const nhType = nhLocked ? originalNh : editedNh;

    try {
      const token = sessionStorage.getItem('token') || localStorage.getItem('token');
      const authHeaders = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const response = await fetch(`/api/clients/${client.id}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({
          nom: name,
          num_chrono: formData.num_chrono,
          matricule_fiscal: formData.vat_code.trim(),
          adresse: formData.adresse.trim(),
          email: adminEmail,
          telephone: formData.administration_phone_number.trim(),
          personne_type: formData.personne_type,
          nh_type: nhType, // E → is_auto=0, NH → is_auto=1
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || data.message || 'Erreur lors de la modification');
      }

      const personnelRes = await fetch(`/api/clients/${client.id}/personnel`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contact: {
            administration_email: adminEmail,
            administration_phone_number: formData.administration_phone_number.trim(),
          },
          manager: {
            first_name: formData.first_name.trim(),
            last_name: formData.last_name.trim(),
            phone_numbers: formData.phone_numbers.trim(),
            emails: mgrEmail,
          },
          identity: {
            identity_type: formData.identity_type,
            identity_number: formData.identity_number.trim(),
            identity_issue_date: formData.identity_issue_date,
            identity_expiray_date: formData.identity_expiray_date,
          },
        }),
      });
      const personnelData = await personnelRes.json();
      if (!personnelRes.ok || !personnelData.success) {
        throw new Error(personnelData.error || 'Erreur lors de l’enregistrement contact/manager');
      }

      // Toujours synchroniser is_auto (sauf si NH/E verrouillé pour non-admin)
      if (!nhLocked) {
        const nhRes = await fetch(`/api/clients/${client.id}/nh-type`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            nh_type: nhType,
            mark_configured: true,
          }),
        });
        const nhData = await nhRes.json().catch(() => ({}));
        if (!nhRes.ok) {
          throw new Error(nhData.error || 'Erreur lors de la mise à jour NH/E (is_auto)');
        }
      }

      await fetch(`/api/clients/${client.id}/personne-type`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ personne_type: formData.personne_type }),
      });

      onClientUpdated();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Erreur lors de la modification');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="caf">
      <form onSubmit={handleSubmit} noValidate>
        {error && (
          <div className="caf-alert" role="alert">
            <i className="fas fa-exclamation-triangle" aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        <nav className="caf-tabs" role="tablist" aria-label="Sections du client">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`caf-tab${activeTab === tab.id ? ' is-active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <i className={`fas ${tab.icon}`} aria-hidden="true" />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>

        <div className="caf-panel" role="tabpanel">
          {loadingData ? (
            <p className="caf-loading">Chargement…</p>
          ) : (
            <>
              {activeTab === 'customer' && (
                <div className="caf-grid">
                  <div className="caf-field caf-field--name">
                    <label htmlFor="cef-name">Nom</label>
                    <input
                      id="cef-name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Nom du client"
                      autoFocus
                      required
                    />
                  </div>
                  <div className="caf-field caf-field--chrono">
                    <label htmlFor="cef-chrono">N° chrono</label>
                    <input
                      id="cef-chrono"
                      name="num_chrono"
                      type="number"
                      min="0"
                      step="1"
                      value={formData.num_chrono}
                      onChange={handleChange}
                      placeholder="N°"
                    />
                  </div>
                  <div className="caf-field">
                    <label htmlFor="cef-vat">Matricule fiscal</label>
                    <input
                      id="cef-vat"
                      name="vat_code"
                      type="text"
                      value={formData.vat_code}
                      onChange={handleChange}
                      placeholder="Matricule fiscal"
                    />
                  </div>
                  <div className="caf-field caf-field--full">
                    <label htmlFor="cef-adresse">Adresse</label>
                    <input
                      id="cef-adresse"
                      name="adresse"
                      type="text"
                      value={formData.adresse}
                      onChange={handleChange}
                      placeholder="Adresse du client"
                    />
                  </div>
                  <div className="caf-field">
                    <span className="caf-label">Type</span>
                    <div className="caf-choice">
                      <label className={`caf-chip${formData.personne_type === 'PP' ? ' is-on' : ''}`}>
                        <input
                          type="radio"
                          name="personne_type"
                          value="PP"
                          checked={formData.personne_type === 'PP'}
                          onChange={handleChange}
                        />
                        <span>Physique</span>
                      </label>
                      <label className={`caf-chip${formData.personne_type === 'PM' ? ' is-on' : ''}`}>
                        <input
                          type="radio"
                          name="personne_type"
                          value="PM"
                          checked={formData.personne_type === 'PM'}
                          onChange={handleChange}
                        />
                        <span>Morale</span>
                      </label>
                    </div>
                  </div>
                  <div className="caf-field caf-field--full">
                    <span className="caf-label">NH / E</span>
                    {nhLocked && (
                      <div className="caf-lock-note" role="status">
                        Type NH/E verrouillé — réservé à l&apos;administrateur.
                      </div>
                    )}
                    <div className="caf-choice">
                      <label
                        className={`caf-chip${formData.nh_type === 'NH' ? ' is-on' : ''}${nhLocked ? ' is-locked' : ''}`}
                      >
                        <input
                          type="radio"
                          name="nh_type"
                          value="NH"
                          checked={formData.nh_type === 'NH'}
                          onChange={handleChange}
                          disabled={nhLocked}
                        />
                        <span>NH</span>
                      </label>
                      <label
                        className={`caf-chip${formData.nh_type === 'E' ? ' is-on' : ''}${nhLocked ? ' is-locked' : ''}`}
                      >
                        <input
                          type="radio"
                          name="nh_type"
                          value="E"
                          checked={formData.nh_type === 'E'}
                          onChange={handleChange}
                          disabled={nhLocked}
                        />
                        <span>E</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'admin' && (
                <div className="caf-grid">
                  <div className="caf-field">
                    <label htmlFor="cef-admin-email">E-mail</label>
                    <input
                      id="cef-admin-email"
                      name="administration_email"
                      type="email"
                      value={formData.administration_email}
                      onChange={handleChange}
                      placeholder="E-mail"
                      autoFocus
                    />
                  </div>
                  <div className="caf-field">
                    <label htmlFor="cef-admin-phone">Téléphone</label>
                    <input
                      id="cef-admin-phone"
                      name="administration_phone_number"
                      type="tel"
                      value={formData.administration_phone_number}
                      onChange={handleChange}
                      placeholder="Téléphone"
                    />
                  </div>
                </div>
              )}

              {activeTab === 'manager' && (
                <div className="caf-grid">
                  <div className="caf-field">
                    <label htmlFor="cef-first">Prénom</label>
                    <input
                      id="cef-first"
                      name="first_name"
                      type="text"
                      value={formData.first_name}
                      onChange={handleChange}
                      placeholder="Prénom"
                      autoFocus
                    />
                  </div>
                  <div className="caf-field">
                    <label htmlFor="cef-last">Nom</label>
                    <input
                      id="cef-last"
                      name="last_name"
                      type="text"
                      value={formData.last_name}
                      onChange={handleChange}
                      placeholder="Nom"
                    />
                  </div>
                  <div className="caf-field">
                    <label htmlFor="cef-mgr-phone">Téléphone</label>
                    <input
                      id="cef-mgr-phone"
                      name="phone_numbers"
                      type="tel"
                      value={formData.phone_numbers}
                      onChange={handleChange}
                      placeholder="Téléphone"
                    />
                  </div>
                  <div className="caf-field">
                    <label htmlFor="cef-mgr-email">E-mail</label>
                    <input
                      id="cef-mgr-email"
                      name="emails"
                      type="email"
                      value={formData.emails}
                      onChange={handleChange}
                      placeholder="E-mail"
                    />
                  </div>
                  <div className="caf-subhead caf-field--full">Identité</div>
                  <div className="caf-field">
                    <label htmlFor="cef-id-type">Type</label>
                    <select
                      id="cef-id-type"
                      name="identity_type"
                      value={formData.identity_type}
                      onChange={handleChange}
                    >
                      <option value="">—</option>
                      <option value="NIC">CIN / NIC</option>
                      <option value="Passport">Passeport</option>
                      <option value="Other">Autre</option>
                    </select>
                  </div>
                  <div className="caf-field">
                    <label htmlFor="cef-id-number">N° identité</label>
                    <input
                      id="cef-id-number"
                      name="identity_number"
                      type="text"
                      value={formData.identity_number}
                      onChange={handleChange}
                      placeholder="N° pièce"
                    />
                  </div>
                  <div className="caf-field">
                    <label htmlFor="cef-id-issue">Date délivrance</label>
                    <input
                      id="cef-id-issue"
                      name="identity_issue_date"
                      type="date"
                      value={formData.identity_issue_date}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="caf-field">
                    <label htmlFor="cef-id-expiry">Date expiration</label>
                    <input
                      id="cef-id-expiry"
                      name="identity_expiray_date"
                      type="date"
                      value={formData.identity_expiray_date}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="caf-actions">
          <button type="button" className="caf-btn caf-btn--ghost" onClick={onCancel} disabled={loading}>
            Annuler
          </button>
          <button type="submit" className="caf-btn caf-btn--primary" disabled={loading || loadingData}>
            {loading ? 'Modification…' : 'Modifier'}
          </button>
        </div>
      </form>
    </div>
  );
}
